/**
 * GameplayPhase Component
 *
 * Renders the main gameplay phase (Phase 3 / ACTIVE). Layout matches
 * MonsterPlacementPhase: board, side monster panels, drawer, submit row.
 *
 * Used by:
 * - GamePage (rendered when gamePhase === ACTIVE)
 *
 * Responsibilities (to be wired):
 * - Movement selection, validation, and socket submit
 * - Turn indicator and game actions
 */

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useAuth } from '../../AuthProvider';
import { useGameStateContext } from '../context/useGameStateContext';
import useMonsters from '../hooks/useMonsters';
import { useBoard } from '../hooks/useBoard';
import { Board } from '../components/Board/Board.tsx';
import { LeftSideDrawer } from '../components/UI/SideDrawer';
import { GameProfile } from '../components/UI/GameProfile';
import { parseBoardState } from '../utils/boardHelpers';
import { getBackground } from '../../utils/images';
import type { PlayerColor } from '../types/game.types';
import type { SquareData } from '../types/board.types';
import type { FrontendMonster, MonsterTypeId } from '../types/monster.types';
import './GamePlay.css';
import MonsterPanel from '../components/Monsters/MonsterPanel.tsx';
import { MonsterDeduction } from '../components/Monsters/MonsterDeduction.tsx';
import { useGameSocket } from '../hooks/useGameSocket.ts';
import type { MoveMonsterResponse } from '../types/socket.types';
import type { MoveRecord } from '../types/game.types';

export interface GameplayPhaseProps {
    /** Room code/name */
    roomCode: string;
}

export const GameplayPhase: React.FC<GameplayPhaseProps> = ({ roomCode }) => {
    const { user } = useAuth();
    const {
        gameState,
        board,
        myPlayerLetter,
        theme,
        playerA,
        playerB,
        isMyTurn,
        currentTurn,
    } = useGameStateContext();
    const { selectedPiece, setSelectedPiece, myMonsters, oppMonsters, updateMonster } = useMonsters();
    const { handleSquareClick, selectedSquare, possibleMoves, setSelectedSquare } = useBoard();
    const { moveMonster, getHistory } = useGameSocket();
    const [viewingTurn, setViewingTurn] = useState<number>(currentTurn);
    const [historyBoard, setHistoryBoard] = useState<typeof board>(null);
    const [historyLastMove, setHistoryLastMove] = useState<MoveRecord | null>(null);
    const latestHistoryRequestTurnRef = useRef<number | null>(null);
    const prevCurrentTurnRef = useRef<number>(currentTurn);

    useEffect(() => {
        const prevCurrentTurn = prevCurrentTurnRef.current;
        const wasViewingPresent = viewingTurn === prevCurrentTurn;

        if (wasViewingPresent && currentTurn !== prevCurrentTurn) {
            // Stay pinned to "present" as new turns arrive.
            setViewingTurn(currentTurn);
        }
        prevCurrentTurnRef.current = currentTurn;

        setViewingTurn((prev) => {
            const clamped = Math.min(prev, currentTurn);
            if (clamped === currentTurn) {
                setHistoryBoard(null);
                setHistoryLastMove(null);
            }
            return clamped;
        });
    }, [currentTurn]);

    useEffect(() => {
        if (viewingTurn === currentTurn) {
            setHistoryBoard(null);
            setHistoryLastMove(null);
            return;
        }

        latestHistoryRequestTurnRef.current = viewingTurn;
        getHistory({ roomCode: roomCode ?? '', turn: viewingTurn }, (res) => {
            if (latestHistoryRequestTurnRef.current !== viewingTurn) return;
            if (res.error || !res.board) return;

            setHistoryBoard(res.board);
            setHistoryLastMove(res.lastMove ?? null);
        });
    }, [viewingTurn, currentTurn, roomCode, getHistory]);

    const liveLastMove = useMemo(() => {
        const moves = gameState?.gameHistory.moves ?? [];
        return moves.length > 0 ? moves[moves.length - 1] : null;
    }, [gameState]);

    const isPresent = viewingTurn === currentTurn;
    const displayBoard = isPresent ? board : historyBoard;
    const displayLastMove = isPresent ? liveLastMove : historyLastMove;

    const squares = useMemo(() => {
        if (!displayBoard) return [];
        return parseBoardState(displayBoard);
    }, [displayBoard]);

    const playerAColor: PlayerColor = (playerA?.color as PlayerColor) ?? 'red';
    const playerBColor: PlayerColor = (playerB?.color as PlayerColor) ?? 'blue';
    const myMonstersColor: PlayerColor = myPlayerLetter === 'A' ? playerAColor : playerBColor;
    const oppMonstersColor: PlayerColor = myPlayerLetter === 'A' ? playerBColor : playerAColor;

    const handleMonsterPanelClick = useCallback(
        (monster: FrontendMonster, _isMine: boolean) => {
            setSelectedPiece(monster);
        },
        [setSelectedPiece]
    );

    const handleDeductionUpdate = useCallback(
        (
            piece: FrontendMonster,
            updates: { xed: MonsterTypeId[]; guess: MonsterTypeId | null }
        ) => {
            updateMonster(piece.id, updates, piece.player);
        },
        [updateMonster]
    );

    const handleGameplaySquareClick = useCallback(
        (square: SquareData) => {
            handleSquareClick(square);
        },
        [handleSquareClick]
    );

    const handleSubmitMove = useCallback(() => {
        if (!selectedPiece || !selectedSquare) return;
        moveMonster(
            {
                monsterId: selectedPiece.id,
                toRow: selectedSquare.row,
                toCol: selectedSquare.col,
            },
            (res: MoveMonsterResponse) => {
                if (res.error) {
                    alert(res.error);
                } else {
                    setSelectedPiece(null);
                    setSelectedSquare(null);
                }
            }
        );
    }, [selectedPiece, selectedSquare, moveMonster, currentTurn, setSelectedPiece, setSelectedSquare]);

    if (!board) {
        return (
            <div
                className="gameplay_container"
                style={{
                    backgroundImage: `url(${getBackground(theme)})`,
                    backgroundSize: 'cover',
                }}
            >
                <p className="gameplay_status">Loading board...</p>
            </div>
        );
    }

    return (
        <div
            className="gameplay_container"
            style={{
                backgroundImage: `url(${getBackground(theme)})`,
                backgroundSize: 'cover',
            }}
        >
            <GameProfile username={user.username} playerLetter={myPlayerLetter} />

            <div className="gameplay_main">
                <p className="gameplay_instructions">
                    {isMyTurn ?
                        `Your turn.`
                        :
                        `Waiting for opponent to move...`
                    }
                </p>
                <div className="gameplay_board_and_monsters_container">
                    <MonsterPanel
                        monsters={myMonsters}
                        selectedPiece={selectedPiece}
                        mine={true}
                        playerColor={myMonstersColor}
                        theme={theme}
                        onMonsterClick={handleMonsterPanelClick}
                    />
                    <div className="gameplay_board_wrapper">
                        <Board
                            squares={squares}
                            onSquareClick={handleGameplaySquareClick}
                            selectedSquare={selectedSquare}
                            selectedPiece={selectedPiece}
                            lastMove={displayLastMove}
                            possibleMoves={isPresent ? possibleMoves : []}
                            theme={theme}
                            playerLetter={myPlayerLetter}
                            playerAColor={playerAColor}
                            playerBColor={playerBColor}
                        />
                    </div>
                    <MonsterPanel
                        monsters={oppMonsters}
                        selectedPiece={selectedPiece}
                        mine={false}
                        playerColor={oppMonstersColor}
                        theme={theme}
                        onMonsterClick={handleMonsterPanelClick}
                    />
                </div>

                <div className="gameplay_submit_section">
                    <div className="gameplay_history_container">
                        <button
                            type="button"
                            className="gameplay_history_button"
                            onClick={() => setViewingTurn((prev) => Math.max(0, prev - 1))}
                            disabled={viewingTurn <= 0}
                        >
                            ←
                        </button>
                        <span>Turn {viewingTurn}</span>
                        <button
                            type="button"
                            className="gameplay_history_button"
                            onClick={() => setViewingTurn((prev) => Math.min(currentTurn, prev + 1))}
                            disabled={viewingTurn >= currentTurn}
                        >
                            →
                        </button>
                    </div>
                    <button
                        type="button"
                        className="gameplay_button"
                        onClick={handleSubmitMove}
                        disabled={!isMyTurn || viewingTurn !== currentTurn}
                    >
                        Submit
                    </button>
                </div>
            </div>

            <LeftSideDrawer title="Deductions" defaultOpen={true}>
                {myPlayerLetter && (
                    <MonsterDeduction
                        selectedPiece={selectedPiece}
                        theme={theme}
                        myPlayerLetter={myPlayerLetter}
                        onUpdateDeduction={handleDeductionUpdate}
                    />
                )}
            </LeftSideDrawer>
        </div>
    );
};

export default GameplayPhase;
